import {QueryClient} from '@components/@query';
import {getUrls} from '@config/endpoints';
import {getEnv} from '@config/getEnv';
import {QueryKey} from '@config/queryKeys';
import {httpAuth} from '@http/httpAuth';
import * as Sentry from '@sentry/react';
import {ActivityEventTypeEnum} from '@services/activity/activity.model';
import {getDocumentReferer} from '@services/activity/getDocumentReferer';
import {getPageId} from '@services/activity/getPageId';
import {IUser} from '@services/user/user.model';
import {getIsPageRefreshed} from '@utils/getIsPageRefreshed';
import {getViewportInfo} from '@utils/getViewportInfo';

export class ActivityServiceBuilder {
    private previousUrl: string | null = null;
    private cachedUserAgent: string | null = null;

    private getUserAgent = () => {
        if (!this.cachedUserAgent) {
            this.cachedUserAgent = `${navigator.userAgent} wehorse-web/${process.env.APP_VERSION}`;
        }
        return this.cachedUserAgent;
    };

    private logActivity = async ({
        eventType,
        metadata,
    }: {
        eventType: ActivityEventTypeEnum;
        metadata: object;
    }) => {
        const user = QueryClient.getQueryData<IUser>([QueryKey.USER]);

        if (!user) {
            return;
        }

        try {
            if (getEnv('NEXT_PUBLIC_E2E')) {
                return;
            }
            const viewport = getViewportInfo();
            await httpAuth.post(
                getUrls().ACTIVITY,
                {
                    eventType,
                    platform: 'web',
                    metadata: {
                        viewport,
                        ...metadata,
                    },
                },
                {headers: {'X-User-Agent': this.getUserAgent()}},
            );
        } catch (e) {
            Sentry.captureException(e);
        }
    };

    logPageView = async (path: string, searchParams?: URLSearchParams) => {
        if (!this.previousUrl) {
            this.previousUrl = getDocumentReferer();
        }

        const isRefresh = !this.previousUrl && getIsPageRefreshed();

        const url = searchParams?.size
            ? `${path}?${searchParams.toString()}`
            : path;

        const fullUrl = getEnv('NEXT_PUBLIC_URL_BASE') + url;

        const pageId = getPageId(url);

        const prevPageId = this.previousUrl
            ? getPageId(this.previousUrl)
            : null;

        if (pageId && !isRefresh) {
            this.logActivity({
                eventType: ActivityEventTypeEnum.PAGE_VIEWED,
                metadata: {
                    page_id: pageId,
                    url_path: url,
                    referrer_page_id: prevPageId,
                    referrer_path: this.previousUrl,
                },
            });
        }

        this.previousUrl = fullUrl;
    };

    logElementClick = async (
        metadata: object,
        eventType?: ActivityEventTypeEnum,
    ) => {
        const path = window.location.pathname;
        const searchParams = window.location.search;
        const url = searchParams ? `${path}${searchParams.toString()}` : path;

        const pageId = getPageId(url);

        this.logActivity({
            eventType: eventType || ActivityEventTypeEnum.ELEMENT_CLICKED,
            metadata: {
                page_id: pageId,
                url_path: url,
                resource_type: 'button',
                ...metadata,
            },
        });
    };

    logComponentViewed = async (metadata: object) => {
        this.logCustomEvent(metadata, ActivityEventTypeEnum.COMPONENT_VIEWED);
    };

    logCustomEvent = async (
        metadata: object,
        eventType: ActivityEventTypeEnum,
    ) => {
        const path = window.location.pathname;
        const searchParams = window.location.search;
        const url = searchParams ? `${path}${searchParams.toString()}` : path;

        const pageId = getPageId(url);

        this.logActivity({
            eventType,
            metadata: {
                page_id: pageId,
                url_path: url,
                ...metadata,
            },
        });
    };
}

export const ActivityService = new ActivityServiceBuilder();
